/**
 * ApplyFixDialog — inline field editor triggered from each data-gap
 * report row. Renders the correct <input> type per registry spec,
 * shows the current value + suggested source URL, and writes via
 * POST /api/admin/data-gap-reports/{id}/apply-fix.
 *
 * On success:
 *   - toast success
 *   - report auto-marks as `actioned` server-side
 *   - parent onSuccess() refetches the queue
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const TYPE_HINT = {
  percent: "%",
  usd: "$",
  gbp: "£",
  int: "",
  float: "",
  text: "",
  bool: "",
  url: "",
};

export default function ApplyFixDialog({ open, onOpenChange, report, registry, onSuccess }) {
  const [newValue, setNewValue] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [auditNote, setAuditNote] = useState("");
  const [busy, setBusy] = useState(false);

  const spec = useMemo(() => {
    if (!report || !registry) return null;
    return registry.find(
      (f) => f.section === report.section && f.label === report.field,
    );
  }, [report, registry]);

  useEffect(() => {
    if (open) {
      setNewValue("");
      // Pre-fill source URL if the hint looks like it contains a URL.
      const hint = report?.hint || "";
      const m = hint.match(/https?:\/\/[^\s)"]+/);
      setSourceUrl(m ? m[0] : "");
      setAuditNote(
        report?.hint
          ? `Applied from family report — ${report.hint.slice(0, 200)}`
          : "",
      );
    }
  }, [open, report]);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (busy || !report || !spec) return;
    setBusy(true);
    try {
      let value = newValue;
      if (spec.type === "bool") value = String(newValue) === "true";
      const { data } = await api.post(
        `/admin/data-gap-reports/${report.id}/apply-fix`,
        {
          new_value: value,
          source_url: sourceUrl || null,
          audit_note: auditNote || null,
        },
      );
      toast.success(
        `Updated ${spec.label} · ${JSON.stringify(data.before)} → ${JSON.stringify(data.after)}`,
      );
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to apply fix");
    } finally {
      setBusy(false);
    }
  }, [busy, report, spec, newValue, sourceUrl, auditNote, onOpenChange, onSuccess]);

  if (!report) return null;

  const notRegistered = !spec;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="apply-fix-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-editorial text-2xl leading-tight text-stone-900">
            Apply fix ·{" "}
            <span className="italic text-purple-800">
              {report.field || "unknown field"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-stone-600">
            {report.degree_title || report.degree_id || "Unknown course"}
            {report.university_name && ` · ${report.university_name}`}
          </DialogDescription>
        </DialogHeader>

        {notRegistered ? (
          <div className="border border-amber-200 bg-amber-50 rounded-md p-3 text-sm text-amber-900 mt-2">
            <div className="font-medium">
              Field not in the inline editor registry yet.
            </div>
            <p className="mt-1 text-[13px] leading-snug">
              <code className="bg-white/60 px-1 rounded">
                ({report.section}, {report.field})
              </code>{" "}
              hasn't been mapped. Fix this value in the DB or extend
              the registry at{" "}
              <code className="bg-white/60 px-1 rounded">
                services/field_registry.py
              </code>
              . Once added it'll be inline-editable from every future
              report on this field.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            {/* Report context */}
            {report.hint && (
              <blockquote className="text-[13px] text-stone-700 bg-stone-50 border-l-2 border-purple-300 pl-3 py-1.5 whitespace-pre-wrap">
                {report.hint}
              </blockquote>
            )}

            <div>
              <Label
                htmlFor="fix-new-value"
                className="text-[11px] font-underground-sc tracking-wider text-stone-700"
              >
                New value{" "}
                <span className="text-stone-400 font-normal font-mono">
                  {spec.type}
                  {spec.min != null || spec.max != null
                    ? ` ${spec.min ?? ""}–${spec.max ?? ""}`
                    : ""}
                </span>
              </Label>
              {spec.type === "bool" ? (
                <Select
                  value={newValue === "" ? undefined : String(newValue)}
                  onValueChange={setNewValue}
                >
                  <SelectTrigger
                    id="fix-new-value"
                    data-testid="apply-fix-value-bool"
                    className="mt-1"
                  >
                    <SelectValue placeholder="Choose true or false" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">true</SelectItem>
                    <SelectItem value="false">false</SelectItem>
                  </SelectContent>
                </Select>
              ) : spec.type === "text" ? (
                <Textarea
                  id="fix-new-value"
                  data-testid="apply-fix-value"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  rows={3}
                  className="mt-1"
                  autoFocus
                />
              ) : (
                <div className="mt-1 relative">
                  {TYPE_HINT[spec.type] && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">
                      {TYPE_HINT[spec.type]}
                    </span>
                  )}
                  <Input
                    id="fix-new-value"
                    data-testid="apply-fix-value"
                    type={
                      ["percent", "usd", "gbp", "int", "float"].includes(spec.type)
                        ? "number"
                        : "text"
                    }
                    step={
                      ["percent", "float"].includes(spec.type) ? "0.01" : "1"
                    }
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    autoFocus
                    className={TYPE_HINT[spec.type] ? "pl-7" : ""}
                  />
                </div>
              )}
              {spec.help && (
                <p className="mt-1 text-[11px] text-stone-500 leading-snug">
                  {spec.help}
                </p>
              )}
            </div>

            <div>
              <Label
                htmlFor="fix-source"
                className="text-[11px] font-underground-sc tracking-wider text-stone-700"
              >
                Source URL{" "}
                <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Input
                id="fix-source"
                data-testid="apply-fix-source"
                type="url"
                placeholder="https://…"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label
                htmlFor="fix-note"
                className="text-[11px] font-underground-sc tracking-wider text-stone-700"
              >
                Audit note{" "}
                <span className="text-stone-400 font-normal">(optional)</span>
              </Label>
              <Textarea
                id="fix-note"
                data-testid="apply-fix-note"
                value={auditNote}
                onChange={(e) => setAuditNote(e.target.value)}
                rows={2}
                className="mt-1"
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={busy}
                data-testid="apply-fix-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy || newValue === ""}
                data-testid="apply-fix-submit"
                className="bg-purple-800 hover:bg-purple-900 text-white"
              >
                {busy ? "Applying…" : "Apply & mark actioned"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
